import React, { createContext, useContext, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * The shared modal shell.
 *
 * Three things were wrong with the hand-rolled dialogs this replaces, and all
 * three showed up as "the corners look bad":
 *
 * 1. **Stacked scrims.** The photo cropper opens *inside* the edit-profile
 *    dialog. Both painted `bg-black/50`, so two overlays compounded to ~75%
 *    black and the backdrop read as a solid slab rather than a dimmed page.
 *    Only the outermost modal paints a scrim now — see `ModalDepth`.
 * 2. **Scrolling on the panel itself.** `overflow-hidden` plus
 *    `overflow-y-auto` on one element makes the whole card the scroll
 *    container, so the tinted header and footer slid under the rounded
 *    corners as it moved. Here the card is a flex column that never scrolls
 *    and only the body between the two bars does, so the radius is always
 *    clipping a stationary edge.
 * 3. **Square footers.** A footer with its own background inside a rounded
 *    parent needs the parent to clip it; that only works if the parent is not
 *    also the scroller. Same fix as (2).
 *
 * Also here because a dialog without them is unfinished: Escape closes,
 * background scroll is locked (reference-counted, so closing a nested dialog
 * does not unlock the page under the one still open), and focus moves into
 * the panel on open.
 *
 * On phones it is a bottom sheet — rounded top, flush bottom, reachable
 * thumb-side. On desktop it is a centred card. Same markup, one breakpoint,
 * matching what `CampaignDetail` already does.
 *
 * **The whole layer is portalled into `<body>`, never rendered in place.**
 * `position: fixed` is measured against the nearest ancestor carrying a
 * transform, filter or `backdrop-filter` — and both profile pages put their
 * content in a `backdrop-blur-xl` card, so `inset-0` covered *the card*
 * instead of the screen. On a phone the layer is `items-end`, which parked
 * every dialog at the bottom edge of that card: on an account with a long
 * gallery the sheet opened a full screen or two below the fold and read as a
 * button that does nothing. Same reason `MediaLightbox` is portalled.
 */

const ModalDepth = createContext(0);

// Reference count rather than a boolean: a nested dialog closing must not
// hand scrolling back to the page while its parent is still open.
let lockCount = 0;

function useBodyScrollLock(active) {
    useEffect(() => {
        if (!active) return undefined;
        if (lockCount === 0) {
            document.body.dataset.prevOverflow = document.body.style.overflow || '';
            document.body.style.overflow = 'hidden';
        }
        lockCount += 1;
        return () => {
            lockCount -= 1;
            if (lockCount === 0) {
                document.body.style.overflow = document.body.dataset.prevOverflow || '';
                delete document.body.dataset.prevOverflow;
            }
        };
    }, [active]);
}

// Which nesting depths currently have a dialog open. Escape used to find the
// topmost dialog by asking whether a panel contained another one — which the
// portal above makes impossible, since a nested dialog is no longer a DOM
// descendant of its parent. Depth still says it: the nested dialog is the one
// with the larger number, exactly as its z-index already assumes.
const openDepths = [];

function useDepthRegistry(open, depth) {
    useEffect(() => {
        if (!open) return undefined;
        openDepths.push(depth);
        return () => {
            const index = openDepths.lastIndexOf(depth);
            if (index !== -1) openDepths.splice(index, 1);
        };
    }, [open, depth]);
}

const topDepth = () => openDepths.reduce((max, value) => Math.max(max, value), 0);

const SIZES = {
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-md',
    lg: 'sm:max-w-lg',
    xl: 'sm:max-w-2xl',
};

function Modal({
    open = true,
    onClose,
    title,
    subtitle,
    children,
    footer,
    size = 'md',
    closeOnBackdrop = true,
    headerAccessory = null,
}) {
    const parentDepth = useContext(ModalDepth);
    const depth = parentDepth + 1;
    const panelRef = useRef(null);
    const backdropDownRef = useRef(false);

    useBodyScrollLock(open);
    useDepthRegistry(open, depth);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (event) => {
            // Only the topmost dialog answers Escape, or one keypress would
            // close the whole stack at once.
            if (event.key === 'Escape' && depth >= topDepth()) {
                event.stopPropagation();
                onClose?.();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose, depth]);

    useEffect(() => {
        if (open) panelRef.current?.focus({ preventScroll: true });
    }, [open]);

    if (!open) return null;

    // Only the bottom of the stack dims the page. Nested dialogs sit on a
    // transparent layer so the darkness never doubles up.
    const isRoot = parentDepth === 0;

    // Into <body>, so the layer is measured against the screen rather than
    // against whichever blurred card happened to render it. Context still
    // crosses a portal, so `ModalDepth` — and with it the scrim, the z-index
    // and Escape — keeps working exactly as before.
    return createPortal(
        <ModalDepth.Provider value={depth}>
            <div
                className={`fixed inset-0 flex items-end sm:items-center justify-center p-0 sm:p-4 ${isRoot ? 'bg-slate-900/60 backdrop-blur-sm modal-scrim' : ''}`}
                style={{ zIndex: 9000 + depth * 10 }}
                // The layer is where a click stops. A portal moves the DOM node
                // but *not* the React tree: a synthetic click still bubbles to
                // whatever component rendered the dialog. The link rows on the
                // profile are click-anywhere cards that open the link in a new
                // tab, so setting a row's thumbnail — every drag of the zoom
                // slider, and the "use this image" button itself — opened the
                // link in a new tab from inside the cropper. A dialog is a
                // top-level layer; nothing behind it should hear its clicks.
                onClick={(event) => event.stopPropagation()}
                // Close on a click that both starts *and* ends on the backdrop:
                // a drag that begins inside the panel (selecting text, dragging
                // the cropper) must not dismiss on release.
                onMouseDown={(event) => { backdropDownRef.current = event.target === event.currentTarget; }}
                onMouseUp={(event) => {
                    if (closeOnBackdrop && backdropDownRef.current && event.target === event.currentTarget) {
                        onClose?.();
                    }
                    backdropDownRef.current = false;
                }}
            >
                <div
                    ref={panelRef}
                    data-modal-panel=""
                    tabIndex={-1}
                    role="dialog"
                    aria-modal="true"
                    aria-label={typeof title === 'string' ? title : undefined}
                    onMouseDown={(event) => event.stopPropagation()}
                    className={`w-full ${SIZES[size] || SIZES.md} bg-white shadow-2xl outline-none
                        flex flex-col overflow-hidden
                        max-h-[92dvh] sm:max-h-[88vh]
                        rounded-t-3xl sm:rounded-3xl modal-panel`}
                >
                    {/* Drag affordance — a sheet with no grabber does not read
                        as dismissible on a phone. Hidden once it is a card. */}
                    <div className="sm:hidden flex justify-center pt-2.5 pb-1 flex-shrink-0">
                        <span className="w-9 h-1 rounded-full bg-slate-300" />
                    </div>

                    {(title || headerAccessory) && (
                        <div className="flex items-start gap-3 px-5 sm:px-6 pt-3 sm:pt-5 pb-3 flex-shrink-0">
                            <div className="min-w-0 flex-1">
                                {title && (
                                    <h2 className="text-lg font-black text-slate-900 leading-snug">{title}</h2>
                                )}
                                {subtitle && (
                                    <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{subtitle}</p>
                                )}
                            </div>
                            {headerAccessory}
                            <button type="button" onClick={onClose} aria-label="Close"
                                className="w-8 h-8 -mr-1 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 flex items-center justify-center transition flex-shrink-0">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none"
                                    viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    )}

                    {/* The only scrolling element. Everything around it stays
                        put, so the rounded corners always clip a still edge. */}
                    <div className="flex-1 overflow-y-auto custom-scrollbar px-5 sm:px-6 pb-5 pt-1">
                        {children}
                    </div>

                    {footer && (
                        <div className="px-5 sm:px-6 py-3.5 bg-slate-50 border-t border-slate-100 flex-shrink-0
                            pb-[max(0.875rem,env(safe-area-inset-bottom))] sm:pb-3.5">
                            {footer}
                        </div>
                    )}
                </div>
            </div>
        </ModalDepth.Provider>,
        document.body,
    );
}

export default Modal;
